

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Validates a quiz config object.
 * Returns null if valid, or an error message string if invalid.
 */
function validateQuizConfig(config) {
  if (!config || typeof config !== 'object')
    return 'Config must be a JSON object'
  if (!config.title || typeof config.title !== 'string')
    return '"title" must be a non-empty string'
  if (!config.intro || typeof config.intro !== 'string')
    return '"intro" must be a non-empty string'
  if (!config.accentColor || typeof config.accentColor !== 'string')
    return '"accentColor" must be a non-empty string'
  if (typeof config.passThreshold !== 'number')
    return '"passThreshold" must be a number'
  if (config.nextAsset !== undefined && typeof config.nextAsset !== 'string')
    return '"nextAsset" must be a string'
  if (
    config.reviewAsset !== undefined &&
    typeof config.reviewAsset !== 'string'
  )
    return '"reviewAsset" must be a string'
  if (!Array.isArray(config.questions) || config.questions.length === 0)
    return '"questions" must be a non-empty array'
  for (var i = 0; i < config.questions.length; i++) {
    var q = config.questions[i]
    if (!q.question || typeof q.question !== 'string')
      return 'Question ' + (i + 1) + ': "question" must be a non-empty string'
    if (!Array.isArray(q.options) || q.options.length < 2)
      return 'Question ' + (i + 1) + ': "options" must have at least 2 items'
    if (
      typeof q.correctIndex !== 'number' ||
      q.correctIndex < 0 ||
      q.correctIndex >= q.options.length
    )
      return 'Question ' + (i + 1) + ': "correctIndex" is out of range'
  }
  return null
}

/**
 * Generates a complete self-contained HTML string for a quiz from a config object.
 * The BAM bridge and CSP are NOT included here — the viewer injects those.
 */
function generateQuizHtml(config) {
  var accentColor = config.accentColor || '#e34c26'
  var title = config.title || 'Quiz'
  var intro = config.intro || ''
  var passThreshold =
    typeof config.passThreshold === 'number' ? config.passThreshold : 80
  var questions = config.questions || []
  var nextAsset = config.nextAsset || ''
  var reviewAsset = config.reviewAsset || ''
  var numQuestions = questions.length

  var questionsHtml = questions
    .map(function (q, index) {
      var qNum = index + 1
      var optionsHtml = q.options
        .map(function (opt, i) {
          return (
            '<label class="radio-option">' +
            '<input type="radio" name="q' +
            qNum +
            '" value="' +
            i +
            '" />' +
            '<span class="radio-circle"></span>' +
            '<span class="radio-label">' +
            escapeHtml(opt) +
            '</span>' +
            '</label>'
          )
        })
        .join('')
      return (
        '<div class="question-container" data-question="' +
        qNum +
        '">' +
        '<div class="question-label">' +
        qNum +
        '. ' +
        escapeHtml(q.question) +
        '</div>' +
        '<div class="radio-group">' +
        optionsHtml +
        '</div>' +
        '<div class="error-message">Please select an answer</div>' +
        '</div>'
      )
    })
    .join('')

  var quizDataJson = JSON.stringify(questions).replace(/</g, '\\u003c')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
  <title>${escapeHtml(title)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg-primary: #ffffff;
      --bg-secondary: #f5f5f5;
      --bg-input: #f0f0f0;
      --bg-header: #2a2a2a;
      --text-primary: #1a1a1a;
      --text-secondary: #555555;
      --text-muted: #888888;
      --text-light: #ffffff;
      --accent-primary: ${accentColor};
      --border-color: #e0e0e0;
      --error-color: ${accentColor};
      --success-color: #2e7d32;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif;
      background-color: var(--bg-primary);
      color: var(--text-primary);
      line-height: 1.5;
      min-height: 100vh;
      padding-bottom: 100px;
    }
    .content { padding: 20px; }
    .section-header {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 1.5px;
      color: var(--text-secondary);
      text-transform: uppercase;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid var(--accent-primary);
    }
    .intro-text {
      color: var(--text-secondary);
      font-size: 14px;
      margin-bottom: 24px;
      line-height: 1.6;
    }
    .question-container { margin-bottom: 28px; }
    .question-label {
      font-size: 15px;
      font-weight: 500;
      color: var(--text-primary);
      margin-bottom: 12px;
      line-height: 1.4;
    }
    .radio-group { display: flex; flex-direction: column; gap: 8px; }
    .radio-option {
      display: flex;
      align-items: flex-start;
      padding: 14px 16px;
      background: var(--bg-input);
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;
    }
    .radio-option:hover { background: #e8e8e8; }
    .radio-option.selected {
      border-color: var(--accent-primary);
      background: rgba(0,0,0,0.05);
    }
    .radio-option input[type='radio'] { display: none; }
    .radio-circle {
      width: 20px;
      height: 20px;
      border: 2px solid var(--text-muted);
      border-radius: 50%;
      margin-right: 12px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      margin-top: 1px;
    }
    .radio-option.selected .radio-circle { border-color: var(--accent-primary); }
    .radio-option.selected .radio-circle::after {
      content: '';
      width: 10px;
      height: 10px;
      background: var(--accent-primary);
      border-radius: 50%;
    }
    .radio-label { font-size: 15px; color: var(--text-primary); line-height: 1.4; }
    .submit-container {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      padding: 16px 20px;
      background: var(--bg-primary);
      border-top: 1px solid var(--border-color);
    }
    .submit-btn {
      width: 100%;
      padding: 18px 20px;
      background: var(--accent-primary);
      color: white;
      border: none;
      border-radius: 10px;
      font-size: 17px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .question-container.error .radio-group {
      border: 1px solid var(--error-color);
      border-radius: 12px;
    }
    .error-message {
      color: var(--error-color);
      font-size: 13px;
      margin-top: 8px;
      display: none;
    }
    .question-container.error .error-message { display: block; }
    .results-overlay {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: var(--bg-primary);
      z-index: 200;
      display: none;
      flex-direction: column;
      overflow-y: auto;
    }
    .results-overlay.visible { display: flex; }
    .results-summary { color: white; padding: 32px 24px; text-align: center; }
    .results-summary.passed {
      background: linear-gradient(135deg, var(--success-color) 0%, #1b5e20 100%);
    }
    .results-summary.failed {
      background: linear-gradient(135deg, var(--accent-primary) 0%, #900 100%);
    }
    .score-circle {
      width: 128px; height: 128px;
      border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      margin: 0 auto 16px;
      border: 4px solid rgba(255,255,255,0.4);
    }
    .score-value { font-size: 42px; font-weight: 700; line-height: 1; }
    .score-label { font-size: 14px; opacity: 0.9; }
    .pass-badge {
      display: inline-block;
      background: rgba(255,255,255,0.25);
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 12px;
    }
    .results-title { font-size: 22px; font-weight: 600; margin-bottom: 8px; }
    .results-subtitle { font-size: 15px; opacity: 0.9; }
    .stats-row {
      display: flex; justify-content: center; gap: 40px;
      margin-top: 20px; padding-top: 20px;
      border-top: 1px solid rgba(255,255,255,0.2);
    }
    .stat-item { text-align: center; }
    .stat-value { font-size: 28px; font-weight: 700; }
    .stat-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; letter-spacing: 0.5px; }
    .results-content { padding: 20px; padding-bottom: 200px; }
    .results-section-header {
      font-size: 13px; font-weight: 700;
      letter-spacing: 1.5px; color: var(--accent-primary);
      text-transform: uppercase; margin-bottom: 16px; margin-top: 8px;
    }
    .question-result {
      display: flex; align-items: flex-start;
      padding: 16px 0; border-bottom: 1px solid var(--border-color);
    }
    .question-result:last-child { border-bottom: none; }
    .result-icon {
      width: 28px; height: 28px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; margin-right: 14px;
      font-size: 14px; font-weight: 700; color: white;
    }
    .result-icon.correct { background: var(--success-color); }
    .result-icon.incorrect { background: var(--accent-primary); }
    .question-info { flex: 1; }
    .question-number {
      font-size: 11px; font-weight: 600; color: var(--text-secondary);
      text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px;
    }
    .question-text { font-size: 15px; color: var(--text-primary); line-height: 1.4; margin-bottom: 8px; }
    .answer-display { font-size: 14px; margin-bottom: 4px; }
    .answer-display.correct { color: var(--success-color); }
    .answer-display.incorrect { color: var(--accent-primary); }
    .blurb { font-size: 13px; color: var(--text-secondary); line-height: 1.5; margin-top: 8px; }
    .actions-container {
      position: fixed; bottom: 0; left: 0; right: 0;
      padding: 16px 20px; background: var(--bg-primary);
      border-top: 1px solid var(--border-color);
      display: flex; flex-direction: column; gap: 10px; z-index: 201;
    }
    .action-btn {
      width: 100%; padding: 16px 20px; border-radius: 10px;
      font-size: 16px; font-weight: 600; cursor: pointer;
      transition: all 0.2s ease; text-align: center;
      text-decoration: none; display: block; border: none;
    }
    .action-btn.primary { background: var(--success-color); color: white; }
    .action-btn.primary.red { background: var(--accent-primary); }
    .action-btn.secondary {
      background: transparent; color: var(--accent-primary);
      border: 2px solid var(--accent-primary);
    }
    .action-btn.tertiary {
      background: transparent; color: var(--text-secondary); padding: 12px 20px;
    }
    .hidden { display: none !important; }
  </style>
</head>
<body>
  <div class="content" id="quiz-content">
    <div class="section-header">${escapeHtml(title)}</div>
    <p class="intro-text">${escapeHtml(intro)}</p>
    <form id="quiz-form">
      ${questionsHtml}
    </form>
  </div>

  <div class="submit-container" id="submit-container">
    <button type="button" class="submit-btn" id="submit-btn" onclick="submitQuiz()">
      Submit Answers
    </button>
  </div>

  <div class="results-overlay" id="results-overlay">
    <div class="results-summary" id="results-summary">
      <div class="score-circle">
        <div class="score-value" id="score-value">0%</div>
        <div class="score-label">Score</div>
      </div>
      <div class="pass-badge" id="pass-badge">&#10003; PASSED</div>
      <div class="results-title" id="results-title">Great Job!</div>
      <div class="results-subtitle" id="results-subtitle">You answered 0 of ${numQuestions} questions correctly</div>
      <div class="stats-row">
        <div class="stat-item">
          <div class="stat-value" id="correct-count">0</div>
          <div class="stat-label">Correct</div>
        </div>
        <div class="stat-item">
          <div class="stat-value" id="incorrect-count">0</div>
          <div class="stat-label">Incorrect</div>
        </div>
      </div>
    </div>
    <div class="results-content" id="results-content">
      <div class="results-section-header">YOUR ANSWERS</div>
      <div id="results-list"></div>
    </div>
    <div class="actions-container" id="actions-container">
      ${
        nextAsset
          ? `<button type="button" class="action-btn primary" id="primary-action"
        onclick="window.BAM && window.BAM.replaceAsset('${nextAsset}')">
        Continue to Next Unit &#8594;
      </button>`
          : ''
      }
      ${
        reviewAsset
          ? `<button type="button" class="action-btn secondary" id="secondary-action"
        onclick="window.BAM && window.BAM.replaceAsset('${reviewAsset}')">
        Review Material
      </button>`
          : ''
      }
      <button class="action-btn tertiary" onclick="restartQuiz()">Retake Quiz</button>
    </div>
  </div>

  <script>
    var quizData = ${quizDataJson};
    var PASS_THRESHOLD = ${passThreshold};
    var NUM_QUESTIONS = ${numQuestions};

    document.querySelectorAll('.radio-option').forEach(function(option) {
      option.addEventListener('click', function() {
        var group = this.closest('.radio-group');
        group.querySelectorAll('.radio-option').forEach(function(opt) {
          opt.classList.remove('selected');
        });
        this.classList.add('selected');
        this.querySelector('input').checked = true;
        this.closest('.question-container').classList.remove('error');
      });
    });

    function submitQuiz() {
      var form = document.getElementById('quiz-form');
      var allAnswered = true;
      var correctCount = 0;
      var userAnswers = [];

      for (var i = 1; i <= NUM_QUESTIONS; i++) {
        var container = document.querySelector('[data-question="' + i + '"]');
        var selected = form.querySelector('input[name="q' + i + '"]:checked');
        if (!selected) {
          container.classList.add('error');
          allAnswered = false;
          userAnswers.push(null);
        } else {
          container.classList.remove('error');
          var answerIndex = parseInt(selected.value, 10);
          userAnswers.push(answerIndex);
          if (answerIndex === quizData[i - 1].correctIndex) {
            correctCount++;
          }
        }
      }

      if (!allAnswered) {
        var firstError = document.querySelector('.question-container.error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var percentage = Math.round((correctCount / NUM_QUESTIONS) * 100);
      var passed = percentage >= PASS_THRESHOLD;

      document.getElementById('score-value').textContent = percentage + '%';
      document.getElementById('correct-count').textContent = correctCount;
      document.getElementById('incorrect-count').textContent = NUM_QUESTIONS - correctCount;
      document.getElementById('results-subtitle').textContent =
        'You answered ' + correctCount + ' of ' + NUM_QUESTIONS + ' questions correctly';

      var summaryEl = document.getElementById('results-summary');
      var badgeEl = document.getElementById('pass-badge');
      var titleEl = document.getElementById('results-title');
      var primaryBtn = document.getElementById('primary-action');

      if (passed) {
        summaryEl.classList.remove('failed');
        summaryEl.classList.add('passed');
        badgeEl.textContent = '\\u2713 PASSED';
        badgeEl.classList.remove('hidden');
        titleEl.textContent = 'Great Job!';
        if (primaryBtn) { primaryBtn.classList.remove('red'); primaryBtn.classList.remove('hidden'); }
      } else {
        summaryEl.classList.remove('passed');
        summaryEl.classList.add('failed');
        badgeEl.classList.add('hidden');
        if (primaryBtn) { primaryBtn.classList.add('hidden'); }
        titleEl.textContent = percentage >= 50 ? 'Good Effort!' : 'Try Again';
      }

      var resultsList = document.getElementById('results-list');
      resultsList.innerHTML = '';
      quizData.forEach(function(q, index) {
        var userAnswer = userAnswers[index];
        var isCorrect = userAnswer === q.correctIndex;
        var answersHTML = '';
        if (isCorrect) {
          answersHTML = '<div class="answer-display correct">\\u2713 ' + q.options[userAnswer] + '</div>';
        } else {
          answersHTML = '<div class="answer-display incorrect">\\u2717 ' + q.options[userAnswer] + '</div>';
          if (passed) {
            answersHTML += '<div class="answer-display correct">\\u2713 ' + q.options[q.correctIndex] + '</div>';
          }
        }
        var blurb = isCorrect ? (q.correctBlurb || '') : (q.incorrectBlurb || '');
        resultsList.innerHTML += (
          '<div class="question-result">' +
          '<div class="result-icon ' + (isCorrect ? 'correct' : 'incorrect') + '">' +
          (isCorrect ? '\\u2713' : '\\u2717') + '</div>' +
          '<div class="question-info">' +
          '<div class="question-number">Question ' + (q.num || (index + 1)) + '</div>' +
          '<div class="question-text">' + q.question + '</div>' +
          answersHTML +
          '<div class="blurb">' + blurb + '</div>' +
          '</div></div>'
        );
      });

      if (window.BAM && window.BAM.submitQuiz) {
        var answers = quizData.map(function(q, i) {
          return {
            question: q.question,
            chosen: userAnswers[i] !== null ? q.options[userAnswers[i]] : 'Not answered',
            correct: userAnswers[i] === q.correctIndex
          };
        });
        window.BAM.submitQuiz({
          title: ${JSON.stringify(title)},
          score: percentage,
          passed: passed,
          correctCount: correctCount,
          totalCount: NUM_QUESTIONS,
          answers: answers
        });
      }

      document.getElementById('results-overlay').classList.add('visible');
      document.getElementById('results-overlay').scrollTop = 0;
    }

    function restartQuiz() {
      document.getElementById('results-overlay').classList.remove('visible');
      document.getElementById('quiz-form').reset();
      document.querySelectorAll('.radio-option').forEach(function(opt) {
        opt.classList.remove('selected');
      });
      document.querySelectorAll('.question-container').forEach(function(c) {
        c.classList.remove('error');
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  </script>
</body>
</html>`
}

module.exports = {
  validateQuizConfig: validateQuizConfig,
  generateQuizHtml: generateQuizHtml,
}
